import React from 'react';

const TierBadge = ({ tier, tierStatus, size = 'md', showLabel = true, textColor: customTextColor, showDate, fullWidth = false }) => {
  const getTierConfig = () => {
    switch (tier) {
      case 'gold':
        return {
          medalGradient: 'from-yellow-300 via-amber-400 to-yellow-500',
          medalShadow: 'shadow-amber-300/50',
          bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50',
          borderColor: 'border-amber-300',
          textColor: '#B45309',
          label: 'Gold',
          medalIcon: (s) => (
            <svg viewBox="0 0 64 64" className={s} fill="none">
              <circle cx="32" cy="36" r="24" fill="url(#goldGrad)" stroke="#B45309" strokeWidth="2" />
              <path d="M32 20l4 8 9 1.5-6.5 6.5 1.5 9L32 40.5 23.5 45l1.5-9L18.5 29.5l9-1.5z" fill="#FDE68A" stroke="#B45309" strokeWidth="1" />
              <path d="M24 8h4l4 8-4 8h-4" fill="#D97706" />
              <path d="M40 8h-4l-4 8 4 8h4" fill="#D97706" />
              <rect x="28" y="4" width="8" height="12" rx="1" fill="#F59E0B" stroke="#B45309" strokeWidth="1" />
              <defs>
                <linearGradient id="goldGrad" x1="8" y1="12" x2="56" y2="60">
                  <stop stopColor="#FDE68A" />
                  <stop offset="0.5" stopColor="#F59E0B" />
                  <stop offset="1" stopColor="#D97706" />
                </linearGradient>
              </defs>
            </svg>
          )
        };
      case 'silver':
        return {
          medalGradient: 'from-gray-200 via-slate-300 to-gray-400',
          medalShadow: 'shadow-slate-300/50',
          bgColor: 'bg-gradient-to-br from-slate-50 to-gray-100',
          borderColor: 'border-slate-300',
          textColor: '#475569',
          label: 'Silver',
          medalIcon: (s) => (
            <svg viewBox="0 0 64 64" className={s} fill="none">
              <circle cx="32" cy="36" r="24" fill="url(#silverGrad)" stroke="#64748B" strokeWidth="2" />
              <path d="M32 20l4 8 9 1.5-6.5 6.5 1.5 9L32 40.5 23.5 45l1.5-9L18.5 29.5l9-1.5z" fill="#E2E8F0" stroke="#64748B" strokeWidth="1" />
              <path d="M24 8h4l4 8-4 8h-4" fill="#94A3B8" />
              <path d="M40 8h-4l-4 8 4 8h4" fill="#94A3B8" />
              <rect x="28" y="4" width="8" height="12" rx="1" fill="#CBD5E1" stroke="#64748B" strokeWidth="1" />
              <defs>
                <linearGradient id="silverGrad" x1="8" y1="12" x2="56" y2="60">
                  <stop stopColor="#E2E8F0" />
                  <stop offset="0.5" stopColor="#94A3B8" />
                  <stop offset="1" stopColor="#64748B" />
                </linearGradient>
              </defs>
            </svg>
          )
        };
      case 'bronze':
        return {
          medalGradient: 'from-orange-300 via-amber-500 to-orange-600',
          medalShadow: 'shadow-orange-300/50',
          bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50',
          borderColor: 'border-orange-300',
          textColor: '#C2410C',
          label: 'Bronze',
          medalIcon: (s) => (
            <svg viewBox="0 0 64 64" className={s} fill="none">
              <circle cx="32" cy="36" r="24" fill="url(#bronzeGrad)" stroke="#C2410C" strokeWidth="2" />
              <path d="M32 20l4 8 9 1.5-6.5 6.5 1.5 9L32 40.5 23.5 45l1.5-9L18.5 29.5l9-1.5z" fill="#FFEDD5" stroke="#C2410C" strokeWidth="1" />
              <path d="M24 8h4l4 8-4 8h-4" fill="#EA580C" />
              <path d="M40 8h-4l-4 8 4 8h4" fill="#EA580C" />
              <rect x="28" y="4" width="8" height="12" rx="1" fill="#F97316" stroke="#C2410C" strokeWidth="1" />
              <defs>
                <linearGradient id="bronzeGrad" x1="8" y1="12" x2="56" y2="60">
                  <stop stopColor="#FFEDD5" />
                  <stop offset="0.5" stopColor="#F97316" />
                  <stop offset="1" stopColor="#EA580C" />
                </linearGradient>
              </defs>
            </svg>
          )
        };
      default:
        return {
          medalGradient: 'from-gray-200 to-gray-300',
          medalShadow: '',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-300',
          textColor: '#6B7280',
          label: 'Unverified',
          medalIcon: (s) => (
            <div className={`${s} rounded-full bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center`}>
              <span className="text-gray-400 text-lg font-bold">?</span>
            </div>
          )
        };
    }
  };

  const config = getTierConfig();
  const isPending = tierStatus === 'pending';

  // Size configurations
  const sizes = {
    sm: { badge: 'text-xs px-2.5 py-1', icon: 'w-5 h-5', gap: 'gap-1.5' },
    md: { badge: 'text-sm px-3 py-1.5', icon: 'w-7 h-7', gap: 'gap-2' },
    lg: { badge: 'text-base px-4 py-2.5', icon: 'w-10 h-10', gap: 'gap-2.5' },
    xl: { badge: 'text-lg px-5 py-3', icon: 'w-16 h-16', gap: 'gap-3' },
    hero: { badge: '', icon: 'w-24 h-24', gap: 'gap-4' }
  };

  const s = sizes[size] || sizes.md;

  // Hero mode: just the medal, large
  if (size === 'hero') {
    return (
      <div className="flex flex-col items-center" data-testid="tier-badge">
        <div className={`${config.medalShadow} shadow-lg rounded-full`}>
          {typeof config.medalIcon === 'function' ? config.medalIcon(s.icon) : (
            <div className={`${s.icon} rounded-full bg-gradient-to-br ${config.medalGradient}`} />
          )}
        </div>
        {showLabel && (
          <div className="mt-2 text-center">
            <span className="text-xl font-bold" style={{ color: customTextColor || config.textColor }}>
              {config.label}
            </span>
            {isPending && (
              <span className="block text-sm text-amber-500 animate-pulse mt-1">Pending Review</span>
            )}
            {showDate && (
              <span className="block text-xs text-gray-400 mt-1">Since {showDate}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${fullWidth ? 'flex w-full justify-center' : 'inline-flex'} items-center ${s.gap} rounded-full ${config.bgColor} ${config.borderColor} border ${s.badge} shadow-sm`}
      data-testid="tier-badge"
    >
      {typeof config.medalIcon === 'function' ? config.medalIcon(s.icon) : (
        <div className={`${s.icon} rounded-full bg-gradient-to-br ${config.medalGradient}`} />
      )}
      {showLabel && (
        <span
          className="font-semibold"
          style={{ color: customTextColor || config.textColor }}
        >
          {config.label}
          {isPending && (
            <span className="ml-1.5 text-amber-500 animate-pulse font-normal">(Pending)</span>
          )}
        </span>
      )}
    </div>
  );
};

export const TierProgress = ({ currentTier, tierStatus }) => {
  const tiers = ['bronze', 'silver', 'gold'];
  const currentIndex = tiers.indexOf(currentTier);
  const isPending = tierStatus === 'pending';

  const tierColors = {
    bronze: { active: 'bg-gradient-to-br from-orange-400 to-orange-600', ring: 'ring-orange-300' },
    silver: { active: 'bg-gradient-to-br from-slate-300 to-slate-500', ring: 'ring-slate-300' },
    gold: { active: 'bg-gradient-to-br from-amber-300 to-amber-500', ring: 'ring-amber-300' }
  };

  return (
    <div className="flex items-center gap-3" data-testid="tier-progress">
      {tiers.map((tier, index) => {
        const isCompleted = index < currentIndex || (index === currentIndex && tierStatus === 'approved');
        const isCurrent = index === currentIndex;
        const isPendingTier = isCurrent && isPending;
        const colors = tierColors[tier];

        return (
          <React.Fragment key={tier}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  isCompleted
                    ? `${colors.active} text-white ring-2 ${colors.ring} shadow-md`
                    : isPendingTier
                    ? `${colors.active} text-white ring-2 ring-amber-400 animate-pulse shadow-md`
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {isCompleted ? '✓' : index + 1}
              </div>
              <span className={`text-xs mt-1.5 capitalize font-medium ${
                isCompleted ? 'text-gray-900' : isPendingTier ? 'text-amber-600' : 'text-gray-400'
              }`}>
                {tier}
              </span>
            </div>
            {index < tiers.length - 1 && (
              <div
                className={`w-14 h-1 rounded-full ${
                  index < currentIndex ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default TierBadge;
